import { useDispatch, useSelector } from 'react-redux';
import { atsJobCreateApiActions } from '@/store/atsJobCreate';
import {
  getPipelineDetails,
  pipelineApiActions,
  pipelineActions,
} from '@/store/atsJobPipeline';
import { useParams } from 'react-router-dom';

export const useJobPipeline = () => {
  const { sort: pipelineSort } = useSelector(getPipelineDetails);
  const dispatch = useDispatch();
  const { jobId } = useParams();

  // Initial pipeline call (filter_by_stage) should ALWAYS use global sort
  // App review sort is only used for stage-specific calls in StageColumnWrapper
  const params = {
    sort_key: pipelineSort?.element,
    sort_order: pipelineSort?.order,
    status: 'active',
  };

  const fetchPipeline = (isBackground = false) => {
    dispatch(
      pipelineApiActions('getKanbanInitialList', {
        requestConfig: { ...params },
        pathParams: { jobId: jobId },
        isBackground,
      })
    );
  };

  const fetchPipelineDependentAPIs = (isBackground = false) => {
    dispatch(
      atsJobCreateApiActions('getInterviewPlan', {
        pathParams: { jobId: jobId },
        isBackground,
      })
    );
    dispatch(
      pipelineApiActions('getRejectedCount', {
        requestConfig: { status: 'rejected' },
        pathParams: { jobId: jobId },
        isBackground,
      })
    );
  };

  const refetchPipelineInBg = () => {
    const { clearAllStageLoadingStates } = pipelineActions;

    // Clear all stage loading states before background refresh to prevent infinite loaders
    dispatch(clearAllStageLoadingStates());

    fetchPipeline(true);
    fetchPipelineDependentAPIs(true);
  };

  /**
   * Same refresh as `refetchPipelineInBg`, but resolves only once every
   * response has landed. `isApi: true` makes dispatch return the request
   * promise instead of an action creator, so the caller can hold a spinner up
   * for the whole group and re-render the board once, with consistent data.
   */
  const refetchPipelineAndWait = (): Promise<unknown> => {
    const { clearAllStageLoadingStates } = pipelineActions;

    dispatch(clearAllStageLoadingStates());

    // isBackground stays true: the grouped spinner replaces the per-request
    // Redux loading states, which would otherwise flash stage-level loaders.
    return Promise.all([
      dispatch(
        pipelineApiActions('getKanbanInitialList', {
          requestConfig: { ...params },
          pathParams: { jobId: jobId },
          isBackground: true,
          isApi: true,
        })
      ),
      dispatch(
        atsJobCreateApiActions('getInterviewPlan', {
          pathParams: { jobId: jobId },
          isBackground: true,
          isApi: true,
        })
      ),
      dispatch(
        pipelineApiActions('getRejectedCount', {
          requestConfig: { status: 'rejected' },
          pathParams: { jobId: jobId },
          isBackground: true,
          isApi: true,
        })
      ),
    ]);
  };

  return {
    refetchPipelineInBg,
    refetchPipelineAndWait,
    fetchPipeline,
    fetchPipelineDependentAPIs,
  };
};
